import CopyToClipboard from "components/Atoms/CopyToClipboard";
import { SectionTemplateHeader } from "components/MainSectionTemplate/SectionTemplateHeader";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import moment from "moment";
import React, { createRef } from "react";
import { useLoaderData } from "react-router";
import { formatWrittenDayMonthYear } from "utilities";
import { ICalendarHelpDetailRow } from "./ICalendarHelpDetailRow";
import { ICalendarHelpLayout } from "./ICalendarHelpLayout";
import { ICalendarHelpStep, ICalendarHelpStepWithButton } from "./ICalendarHelpStep";
import { RecordsCountMessage } from "ICalendarFeed/RecordsCountMessage";

const ICalendarHelpApp = () => {
  const viewModel = useLoaderData<server.viewmodel.ICalendarFeedHelpFrontModel>();

  const iCalendarFeedRef = createRef<HTMLDivElement>();
  const subscribeOnIphoneRef = createRef<HTMLDivElement>();
  const subscribeOnAndroidRef = createRef<HTMLDivElement>();
  const subscribeOnMacOsRef = createRef<HTMLDivElement>();
  const subscribeInGoogleCalendarRef = createRef<HTMLDivElement>();

  return <ICalendarHelpLayout sidebar={<div className="s1-linked-items-row gap-mini flex-column">
    <a onClick={() => {
      iCalendarFeedRef.current?.scrollIntoView({ behavior: "smooth" });
    }} style={{ textDecoration: "none" }} className="hoverable flex">iCalendar feed</a>
    <a onClick={() => {
      subscribeOnIphoneRef.current?.scrollIntoView({ behavior: "smooth" });
    }} style={{ textDecoration: "none" }} className="hoverable flex">Subscribe on iPhone/iPad</a>
    <a onClick={() => {
      subscribeOnAndroidRef.current?.scrollIntoView({ behavior: "smooth" });
    }} style={{ textDecoration: "none" }} className="hoverable flex">Subscribe on Android</a>
    <a onClick={() => {
      subscribeOnMacOsRef.current?.scrollIntoView({ behavior: "smooth" });
    }} style={{ textDecoration: "none" }} className="hoverable flex">Subscribe on Mac OS</a>
    <a onClick={() => {
      subscribeInGoogleCalendarRef.current?.scrollIntoView({ behavior: "smooth" });
    }} style={{ textDecoration: "none" }} className="hoverable flex">Subscribe in Google Calendar</a>
  </div>}>
    <SectionPanel.Section key="section">
      <SectionPanel.Panel
        panelRef={iCalendarFeedRef}
        id="icalendar-feed"
        header={<SectionTemplateHeader>iCalendar feed</SectionTemplateHeader>}
      >
        <ICalendarHelpDetailRow label="Unique calendar URL">
          <CopyToClipboard value={viewModel.feedUrl} />
        </ICalendarHelpDetailRow>

        <ICalendarHelpDetailRow gap label="Feed owner">
          {viewModel.iCalendarFeed.owner.user.fullName}
        </ICalendarHelpDetailRow>

        <ICalendarHelpDetailRow gap label="Events in feed">
          <RecordsCountMessage isLoading={false} count={viewModel.itemsInFeedCount} />
        </ICalendarHelpDetailRow>

        <h3 className="icalendar-feed-help-appointments">Include appointments</h3>

        <ICalendarHelpDetailRow gap label="Confirmed">
          Always
        </ICalendarHelpDetailRow>

        <ICalendarHelpDetailRow gap label="Option">
          {viewModel.iCalendarFeed.includeOptions ? "Yes" : "No"}
        </ICalendarHelpDetailRow>

        <ICalendarHelpDetailRow gap label="Canceled">
          {viewModel.iCalendarFeed.includeCancelled ? "Yes" : "No"}
        </ICalendarHelpDetailRow>

        <ICalendarHelpDetailRow gap label="Flights">
          {viewModel.iCalendarFeed.includeFlight ? "Yes" : "No"}
        </ICalendarHelpDetailRow>

        <ICalendarHelpDetailRow gap label="Occurring after">
          {formatWrittenDayMonthYear(moment.utc().subtract(viewModel.iCalendarFeed.selectionStartSubtractDays, "days"))}
        </ICalendarHelpDetailRow>
      </SectionPanel.Panel>

      <SectionPanel.Panel
        panelRef={subscribeOnIphoneRef}
        id="subscribe-on-iphone"
        header={<SectionTemplateHeader>Subscribe on iPhone/iPad</SectionTemplateHeader>}
      >
        <div className="flex-column gap-larger">
          <div className="flex-column gap-larger">
            <ICalendarHelpStepWithButton
              label="Step 1"
              buttonUrl={viewModel.webCalUrl}
            />

            <ICalendarHelpStep
              label="Step 2"
              text={`ICal is launched and shows a dialog. Click "Subscribe".`}
              imageName="iphone_step_2.png"
            />

            <ICalendarHelpStep
              label="Step 3"
              text={`Make sure Auto-refresh is set to "Every 15 minutes". Click "OK".`}
              imageName="iphone_step_3.png"
            />

            <ICalendarHelpStep
              label="Finished"
              text="You have successfully subscribed to the feed."
            />
          </div>
        </div>
      </SectionPanel.Panel>

      <SectionPanel.Panel
        panelRef={subscribeOnAndroidRef}
        id="subscribe-on-android"
        header={<SectionTemplateHeader>Subscribe on Android</SectionTemplateHeader>}
      >
        <div className="flex-column gap-larger">
          <ICalendarHelpStep
            label="Step 1"
            text={<>Install the <a href="https://play.google.com/store/apps/details?id=com.google.android.calendar">Google Calendar app</a> from the Play store.</>}
          />

          <ICalendarHelpStep
            label="Step 2"
            text={<>Use a desktop computer to sign in to <a href="https://calendar.google.com/">Google Calendar</a>. Use the account that is connected to your Android device.</>}
          />

          <ICalendarHelpStep
            label="Step 3"
            text={<>Continue with <a onClick={() => {
              subscribeInGoogleCalendarRef.current?.scrollIntoView({ behavior: "smooth" });
            }}>our instruction</a> on how to add the SystemOne calendar in Google Calendar.</>}
          />

          <ICalendarHelpStep
            label="Finished"
            text="Once you add the SystemOne calendar to your Google account, it should also show up in your mobile app. This might take up to a few hours."
          />
        </div>
      </SectionPanel.Panel>

      <SectionPanel.Panel
        panelRef={subscribeOnMacOsRef}
        id="subscribe-on-macos"
        header={<SectionTemplateHeader>Subscribe on Mac OS</SectionTemplateHeader>}
      >
        <div className="flex-column gap-larger">
          <ICalendarHelpStepWithButton
            label="Step 1"
            buttonUrl={viewModel.webCalUrl}
          />

          <ICalendarHelpStep
            label="Step 2"
            text={`ICal is launched and shows a dialog. Click "Subscribe".`}
            imageName="mac_step_2.png"
          />

          <ICalendarHelpStep
            label="Step 3"
            text={`Make sure Auto-refresh is set to "Every 15 minutes". Click "OK".`}
            imageName="mac_step_3.png"
          />

          <ICalendarHelpStep
            label="Finished"
            text="You have successfully subscribed to the feed."
          />
        </div>
      </SectionPanel.Panel>

      <SectionPanel.Panel
        panelRef={subscribeInGoogleCalendarRef}
        id="subscribe-in-google-calendar"
        header={<SectionTemplateHeader>Subscribe in Google Calendar</SectionTemplateHeader>}
      >
        <div className="flex-column gap-larger">
          <ICalendarHelpStep
            label="Step 1"
            text={<div className="flex-column gap-small">
              <div>Copy the calendar URL below to your clipboard.</div>
              <CopyToClipboard value={viewModel.feedUrl} />
            </div>}
          />

          <ICalendarHelpStep
            label="Step 2"
            text="In Google Calendar click the circle with the plus next to Other calendars."
            imageName="google_step_2.png"
          />

          <ICalendarHelpStep
            label="Step 3"
            text={`A context menu opens. Click "From URL".`}
            imageName="google_step_3.png"
          />

          <ICalendarHelpStep
            label="Step 4"
            text={`Paste the calendar URL from your clipboard in the "From URL" dialog and click "Add Calendar".`}
            imageName="google_step_4.png"
          />

          <ICalendarHelpStep
            label="Finished"
            text="Events appear in Google Calendar and will be refreshed automatically. It might take a few minutes before data shows up."
          />
        </div>
      </SectionPanel.Panel>
    </SectionPanel.Section>
  </ICalendarHelpLayout>
}

export default ICalendarHelpApp;